import * as React from "react";
import { isSameDay, startOfDay } from "date-fns";
import { useSalonToday } from "@/hooks/me/use-salon";
import { Calendar } from "./appointments-calendar";
import { AppointmentsToolbar } from "./appointments-toolbar";
import { AddAppointmentDialog } from "./add-appointment-dialog";
import { EditAppointmentDialog } from "./edit-appointment-dialog";
import { OverrideRosterDialog } from "./override-roster-dialog";
import { DataError } from "@/components/common/error/data-error";
import type { Staff } from "@/types/appointments/calendar-types";
import type { CalendarEditableAppointment } from "@/types/appointments/appointment-form-types";
import {
  useGetStaffPaginated,
  useGetStaffServicesSummary,
} from "@/hooks/staff/use-staff";
import {
  useGetAppointments,
  useGetAvailability,
  useUpdateAppointment,
} from "@/hooks/appointments/use-appointments";
import { toCalendarBlocks } from "@/mappers/appointments/appointment-to-calendar-blocks";
import { toCalendarStaff } from "@/mappers/appointments/staff-to-calendar-staff";
import { toAppointmentUpdateFromLineMove } from "@/mappers/appointments/appointment-update-from-line-move";
import {
  formatSlotTime,
  lineEndsAfterMidnight,
  rangesOverlap,
  rowIndexToTime,
  workingHoursToOpeningHours,
} from "@/lib/appointments/calendar-utils";
import {
  APPOINTMENT_TOAST_DURATION_MS,
  appointmentToastMessage,
} from "@/lib/appointments/appointment-toast";
import { toast } from "sonner";
import { useIntl } from "react-intl";

export function AppointmentsPage() {
  const intl = useIntl();
  const today = useSalonToday();
  const [date, setDate] = React.useState<Date>(today);
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [selectedSlot, setSelectedSlot] = React.useState<{
    staff_id: number;
    rowIndex: number;
  } | null>(null);
  const [editDialogOpen, setEditDialogOpen] = React.useState(false);
  const [editingAppointment, setEditingAppointment] =
    React.useState<CalendarEditableAppointment | null>(null);
  const [overrideStaff, setOverrideStaff] = React.useState<Staff | null>(null);

  const { data: staffResponse } = useGetStaffPaginated({
    active_only: true,
    size: 100,
  });
  const {
    data: availabilityResponse,
    error: availabilityError,
  } = useGetAvailability(date);
  const {
    data: appointmentsResponse,
    error: appointmentsError,
  } = useGetAppointments(date);
  const {
    data: servicesSummary,
    error: servicesSummaryError,
  } = useGetStaffServicesSummary();
  const updateAppointment = useUpdateAppointment();

  const staff: Staff[] = React.useMemo(
    () =>
      toCalendarStaff(
        staffResponse?.items ?? [],
        availabilityResponse?.staff
      ),
    [staffResponse, availabilityResponse]
  );

  const appointments = React.useMemo(
    () =>
      toCalendarBlocks(
        (appointmentsResponse?.items ?? []).filter(
          (item) => item.status !== "cancelled"
        )
      ),
    [appointmentsResponse]
  );

  const handleAddAppointment = React.useCallback(
    (staffId: string, rowIndex: number) => {
      setSelectedSlot({ staff_id: Number(staffId), rowIndex });
      setDialogOpen(true);
    },
    []
  );

  const handleEditAppointment = React.useCallback(
    (appointmentId: number) => {
      const appointment = appointmentsResponse?.items.find(
        (item) => item.id === appointmentId
      );
      if (!appointment) return;
      if (appointment.status === "completed") return;
      setEditingAppointment({
        ...appointment,
        status: appointment.status,
      });
      setEditDialogOpen(true);
    },
    [appointmentsResponse]
  );

  const handleAppointmentUpdate = React.useCallback(
    (
      lineId: number,
      appointmentId: number,
      newStaffId: string,
      newStartHour?: number,
      newStartMinute?: number,
      newDurationMinutes?: number
    ) => {
      const appointment = appointmentsResponse?.items.find(
        (item) => item.id === appointmentId
      );
      if (!appointment) return;
      if (appointment.status === "completed") return;

      const line = appointment.services.find((item) => item.id === lineId);
      if (!line) return;

      const targetStaffId = Number(newStaffId);
      const patch: {
        staff_id: number;
        start_time?: string;
        duration_minutes?: number;
        price?: number;
      } = { staff_id: targetStaffId };
      if (newStartHour !== undefined && newStartMinute !== undefined) {
        patch.start_time = formatSlotTime(newStartHour, newStartMinute);
      }

      // Dropped on a different column: only persist if that stylist is assigned
      // this service. Returning without mutate
      // leaves the query data unchanged, so the block snaps back.
      if (targetStaffId !== line.staff_id) {
        if (!servicesSummary) {
          toast.error("Could not verify stylist services");
          return;
        }
        const targetSummary = servicesSummary.staff.find(
          (entry) => entry.staff_id === targetStaffId
        );
        const summaryService = targetSummary?.services.find(
          (item) => item.service_id === line.service_id
        );
        if (!summaryService) {
          const targetStaff = staffResponse?.items.find(
            (member) => member.id === targetStaffId
          );
          const stylistName = targetStaff
            ? targetStaff.preferred_name ||
              `${targetStaff.first_name} ${targetStaff.last_name}`
            : "This stylist";
          toast.error(
            `${stylistName} does not do ${line.service_name ?? "this service"}`
          );
          return;
        }
        if (
          summaryService.duration_minutes == null ||
          summaryService.price == null
        ) {
          toast.error("This service is not priced for this stylist's level");
          return;
        }
        // Use the target stylist's duration/price, not the original line's.
        patch.duration_minutes = summaryService.duration_minutes;
        patch.price = summaryService.price;
      } else if (newDurationMinutes !== undefined) {
        patch.duration_minutes = newDurationMinutes;
      }

      const proposedStart = patch.start_time ?? line.start_time;
      const proposedDuration = patch.duration_minutes ?? line.duration_minutes;
      if (lineEndsAfterMidnight(proposedStart, proposedDuration)) {
        toast.error("That booking would run past midnight");
        return;
      }
      const overlapsBusy = (appointmentsResponse?.items ?? []).some((item) => {
        if (item.status === "cancelled" || item.status === "no_show") {
          return false;
        }
        return item.services.some((other) => {
          if (other.id === lineId) return false;
          if (other.staff_id !== targetStaffId) return false;
          return rangesOverlap(
            proposedStart,
            proposedDuration,
            other.start_time,
            other.duration_minutes
          );
        });
      });
      if (overlapsBusy) {
        toast.error("That overlaps another booking");
        return;
      }

      updateAppointment.mutate(
        {
          id: appointment.id,
          data: toAppointmentUpdateFromLineMove(appointment, lineId, patch),
        },
        {
          onError: () => {
            toast.error(
              appointmentToastMessage({
                intl,
                action: "updated",
                variant: "error",
                clientName: appointment.client_name,
                appointmentDate: appointment.appointment_date,
              }),
              { duration: APPOINTMENT_TOAST_DURATION_MS }
            );
          },
        }
      );
    },
    [
      appointmentsResponse,
      servicesSummary,
      staffResponse,
      updateAppointment,
    ]
  );

  const openingHours = React.useMemo(
    () =>
      workingHoursToOpeningHours(staff.flatMap((s) => s.working_hours)),
    [staff]
  );
  const isPastDay = startOfDay(date) < today;

  const selectedStaff = staffResponse?.items.find(
    (s) => s.id === selectedSlot?.staff_id
  );
  const slotTime = selectedSlot
    ? rowIndexToTime(selectedSlot.rowIndex)
    : null;
  const start_time = slotTime
    ? formatSlotTime(slotTime.hour, slotTime.minute)
    : "";

  return (
    <div className="h-full flex flex-col">
      <AppointmentsToolbar date={date} onDateChange={setDate} />
      <div className="flex-1">
        {appointmentsError ? (
          <DataError topic="appointments" />
        ) : availabilityError ? (
          <DataError topic="availability" />
        ) : servicesSummaryError ? (
          <DataError topic="stylist services" />
        ) : (
          <Calendar
            staff={staff}
            openingHours={openingHours}
            appointments={appointments}
            onAppointmentUpdate={handleAppointmentUpdate}
            onAddAppointment={handleAddAppointment}
            onEditAppointment={handleEditAppointment}
            onStaffHeaderClick={setOverrideStaff}
            readOnly={isPastDay}
          />
        )}
      </div>
      {selectedSlot && selectedStaff && (
        <AddAppointmentDialog
          key={`${selectedSlot.staff_id}-${start_time}-${date.toDateString()}`}
          open={dialogOpen}
          onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) {
              setSelectedSlot(null);
            }
          }}
          onCreated={(savedDate) => {
            if (!isSameDay(savedDate, date)) {
              setDate(savedDate);
            }
          }}
          appointment_date={date}
          staff_id={selectedStaff.id}
          start_time={start_time}
        />
      )}
      {editingAppointment && (
        <EditAppointmentDialog
          key={editingAppointment.id}
          appointment={editingAppointment}
          open={editDialogOpen}
          onOpenChange={(open) => {
            setEditDialogOpen(open);
            if (!open) {
              setEditingAppointment(null);
            }
          }}
          onUpdated={(savedDate) => {
            if (!isSameDay(savedDate, date)) {
              setDate(savedDate);
            }
          }}
        />
      )}
      {overrideStaff && (
        <OverrideRosterDialog
          key={`${overrideStaff.id}-${date.toDateString()}`}
          open
          onOpenChange={(open) => {
            if (!open) {
              setOverrideStaff(null);
            }
          }}
          staffId={Number(overrideStaff.id)}
          staffName={overrideStaff.name}
          date={date}
          workingHours={overrideStaff.working_hours}
        />
      )}
    </div>
  );
}
